// MISB ST 0604 timestamps in H.264 / H.265 user_data_unregistered SEI messages (payload type 5).
// hls.js parses the SEI messages and reports them with FRAG_PARSING_USERDATA ({pts, uuid, userDataBytes}).
//
// Payload after the UUID (12 bytes): status, t0 t1, 0xFF, t2 t3, 0xFF, t4 t5, 0xFF, t6 t7.
// The 64-bit value is microseconds since 1970 (nanoseconds with the H.265 nano UUID). Some encoders write
// milliseconds into the same layout and other bytes in place of 0xFF, so the separators aren't checked and
// the unit is the one that gives the time closest to now.

const UUIDS = {
	// H.264 / H.262: ASCII "MISPmicrosectime"
	"4d495350-6d69-6372-6f73-656374696d65": "micro",
	// H.265: version 3 UUID of "MISPmicrosectime-v2"
	"a8687dd4-d759-3758-a5ce-f0338b6545f1": "micro",
	// H.265: version 3 UUID of "MISPnanosectime-v1"
	"cf848278-ee23-306c-9265-e8fef22fb8b8": "nano"
};

const MAX_CLOCK_DIFF_MS = 24 * 3600 * 1000;

// Epoch milliseconds of the frame, or null when the sample isn't a usable ST 0604 timestamp
export function misbTimestampMs(uuid, bytes, now_ms) {
	var kind = UUIDS[uuid];
	if (!kind || !bytes || bytes.length < 12) return null;
	var hi = ((bytes[1] << 24) >>> 0) + (bytes[2] << 16) + (bytes[4] << 8) + bytes[5];
	var lo = ((bytes[7] << 24) >>> 0) + (bytes[8] << 16) + (bytes[10] << 8) + bytes[11];
	// Precise enough: microseconds fit in 2^53, nanoseconds lose well under a microsecond
	var value = hi * 4294967296 + lo;
	var candidates = kind === "nano" ? [value / 1e6] : [value / 1000, value, value / 1e6];
	var best = null;
	candidates.forEach((ms) => {
		if (best === null || Math.abs(ms - now_ms) < Math.abs(best - now_ms)) best = ms;
	});
	return Math.abs(best - now_ms) <= MAX_CLOCK_DIFF_MS ? best : null;
}

const MAX_SAMPLES = 2000;

// Frame timestamps by media time; latency() compares the shown frame to the local clock.
// With the arrival time of each fragment, breakdown() splits the latency into the time a frame waits for
// its segment to be complete, the delivery of the segment and the player buffer.
export class MisbLatency {
	constructor () {
		// [{pts: media time in seconds, ms: epoch ms, sn}] sorted by pts
		this.samples = [];
		// sn -> {arrival: epoch ms when loaded, last: capture time of the last frame}
		this.fragments = {};
	}
	// frag: {sn, arrival} of the fragment the samples came from (optional)
	add_userdata (samples, frag) {
		var now = Date.now();
		var added = false;
		(samples || []).forEach((s) => {
			if (s.payloadType !== 5 || !isFinite(s.pts)) return;
			var ms = misbTimestampMs(s.uuid, s.userDataBytes, now);
			if (ms === null) return;
			this.samples.push({pts: s.pts, ms: ms, sn: frag ? frag.sn : null});
			if (frag) {
				var f = this.fragments[frag.sn] || (this.fragments[frag.sn] = {arrival: frag.arrival, last: ms, duration: frag.duration});
				if (ms > f.last) f.last = ms;
			}
			added = true;
		});
		if (!added) return;
		this.samples.sort((a, b) => a.pts - b.pts);
		if (this.samples.length > MAX_SAMPLES) this.samples.splice(0, this.samples.length - MAX_SAMPLES);
		var sns = Object.keys(this.fragments);
		if (sns.length > 60) {
			var keep = {};
			this.samples.forEach(s => { if (s.sn !== null) keep[s.sn] = true; });
			sns.forEach(sn => { if (!keep[sn]) delete this.fragments[sn]; });
		}
	}
	find (t) {
		var s = this.samples;
		var lo = 0;
		var hi = s.length - 1;
		var found = -1;
		while (lo <= hi) {
			var mid = (lo + hi) >> 1;
			if (s[mid].pts <= t) {
				found = mid;
				lo = mid + 1;
			} else {
				hi = mid - 1;
			}
		}
		return found < 0 || t - s[found].pts > 2 ? null : s[found];
	}
	// Milliseconds from capture to now for the frame at media time t, or null without a timestamp near it
	latency (t, now_ms) {
		var s = this.find(t);
		if (!s) return null;
		return now_ms - (s.ms + (t - s.pts) * 1000);
	}
	// {total, segment, delivery, buffer, segment_length} in ms for the frame at media time t;
	// segment, delivery and buffer add up to total
	breakdown (t, now_ms) {
		var s = this.find(t);
		if (!s) return null;
		var f = s.sn !== null ? this.fragments[s.sn] : null;
		var frame_ms = s.ms + (t - s.pts) * 1000;
		if (!f) return {total: now_ms - frame_ms};
		return {
			total: now_ms - frame_ms,
			segment: Math.max(0, f.last - frame_ms),
			delivery: f.arrival - f.last,
			buffer: now_ms - f.arrival,
			segment_length: isFinite(f.duration) ? f.duration * 1000 : null
		};
	}
	reset () {
		this.samples = [];
		this.fragments = {};
	}
}
